import type { Step } from "react-joyride";

/**
 * Tour step definitions for the post-auth Homepage
 * (`pages/WorkflowTypeSelectorPage.tsx`).
 *
 * This is an APP-LEVEL page tour (not a workflow stage). It walks the
 * chat-first entry first (title -> starting prompts), then the navbar
 * Feedback button, and ends on the Home chatbot (the homepage AI assistant) —
 * analogous to how each workflow's first-stage tour ends on the in-workflow
 * FarChat assistant. The single tour covers BOTH homepage checklist items:
 * workflow category & type selection AND the Home chatbot.
 *
 * TWO INTERACTION GATES drive the tail of the tour, in this order:
 *
 *  MOBILE-NAV-OPEN gate (`mobileNavigation`) — phone navigation nests Feedback
 *  inside the single hamburger menu. On those viewports this step first asks the
 *  user to open that menu, then advances to the existing Feedback gate once its
 *  menu item is mounted.
 *
 *  FEEDBACK-OPEN gate (`feedbackOpen`) — FIRST. The "Share your feedback" step
 *  highlights the navbar Feedback button. Its Next is greyed out until the
 *  feedback dialog is open (`feedback-dialog` present in the DOM). The follow-up
 *  in-form step is only appended once `feedbackOpen` is true; it is NOT the
 *  terminal step (the chat gate follows), so its footer reads "Next" and closing
 *  the form (Cancel) advances the tour to the chat gate (see HomepageTour's
 *  CLOSE-auto-advance, keyed off `data.panelOf: "feedback"`).
 *
 *  CHAT-OPEN gate (`chatOpen`) — LAST. The "Ask the assistant" step highlights
 *  the homepage chat entry surface and prompts the user to open the chat. Its
 *  Next is greyed out (via `requiresInteraction`) until the chat panel is open
 *  (its root, `home-chat-panel`, is present in the DOM). The follow-up "Your
 *  homepage assistant" step — which targets the open panel — is only appended
 *  once `chatOpen` is true and is the TERMINAL step (footer reads "Done").
 *
 * Opening Home Chat expands the inline homepage workspace. Opening feedback
 * mounts its existing dialog. In both cases the tour advances to the appended
 * panel step when the target appears — see `HomepageTour.tsx`.
 *
 * Gating is generic and data-driven: a step opts in with
 * `data: { requiresInteraction: true, gateId: "chat" | "feedback" }` and the
 * tour component computes `advanceBlocked` for the current step off its
 * `gateId`; `TourFooter` greys out the primary button when blocked.
 */

/**
 * The in-panel detail step, revealed once the Home chat panel is open. The tour
 * auto-advances here when the chat opens (see HomepageTour). It is the TERMINAL
 * step of the tour (the chat gate comes last, after the feedback gate), so it
 * carries `locale.last` to label its primary button "Done".
 */
export const homeChatPanelStep: Step = {
  target: '[data-tour-id="home-chat-panel"]',
  // Center the tour bubble over the large inline conversation workspace so it
  // remains fully in-frame on desktop and mobile.
  placement: "center",
  title: "Your homepage assistant",
  content:
    "This is your homepage assistant. Ask any FAR question — and for each question you choose which FAR version(s) to use: Revolutionary FAR Overhaul (RFO), Legacy FAR, or Both. If a question is vague, the assistant proposes a few clarifications before answering. You can even attach documents for it to read. Past conversations are saved in the left sidebar, so you can pick up where you left off.",
  skipBeacon: true,
  locale: { last: "Done" },
  // Marks the chat in-form step so HomepageTour can close the chat window when
  // the user navigates BACK out of it (finishing the tour leaves it open).
  data: { panelOf: "chat" },
};

/**
 * Feedback-button gate. Highlights the navbar Feedback button and asks the user
 * to open the feedback form. Next is greyed out (requiresInteraction) until the
 * feedback dialog is open — see HomepageTour's `feedbackOpen` gate.
 */
export const feedbackButtonStep: Step = {
  target: '[data-tour-id="feedback-button"]',
  placement: "bottom",
  title: "Share your feedback",
  content:
    "Spotted something or have an idea? Click the Feedback button to open the form — you can send us a note from any page.",
  skipBeacon: true,
  data: { requiresInteraction: true, gateId: "feedback" },
};

/**
 * Phone-only navigation-menu gate. The compact navbar places Feedback inside
 * this menu, so this step is inserted immediately before `feedbackButtonStep`
 * on phone viewports. Its target is always mounted, while the Feedback target
 * is mounted only after this menu opens.
 */
export const mobileNavigationMenuStep: Step = {
  target: '[data-tour-id="mobile-nav-menu"]',
  placement: "bottom-end",
  title: "Open navigation",
  content:
    "On a phone, account options and Feedback are in this menu. Open it to continue the walkthrough.",
  skipBeacon: true,
  data: { requiresInteraction: true, gateId: "mobile-nav" },
};

/**
 * In-form step, revealed once the feedback dialog is open (the tour auto-advances
 * here on open, mirroring the chat panel). NOT the terminal step — the chat gate
 * follows — so it carries no `locale.last` (the footer shows "Next"). The
 * `panelOf: "feedback"` marker lets HomepageTour's CLOSE-auto-advance effect
 * detect when this step is the active one and move the tour onward when the user
 * clicks Cancel.
 */
export const feedbackPanelStep: Step = {
  target: '[data-tour-id="feedback-dialog"]',
  placement: "left",
  title: "Tell us what you think",
  content:
    "Type your feedback here and hit Send whenever you like — it reaches our team along with the page you're on. You can also upload screenshots or other files to help us understand your feedback. For now, click Cancel to close this window and we'll continue the tour.",
  skipBeacon: true,
  data: { panelOf: "feedback" },
};

export const getSteps = (
  chatOpen = false,
  feedbackOpen = false,
  canUseChatbot = true,
  mobileNavigation = false
): Step[] => {
  const steps: Step[] = [
    {
      target: '[data-tour-id="home-tour-title"]',
      placement: "bottom",
      title: "Welcome to your homepage",
      content: canUseChatbot
        ? "This is your procurement desk. Start by describing the work you need to do, or use one of the common starting points below the message box."
        : "This is your procurement desk. Reopen saved work here, or use the navigation bar to manage all of your workflows.",
      skipBeacon: true,
    },
  ];

  if (canUseChatbot) {
    steps.push({
      target: '[data-tour-id="home-tour-starters"]',
      // We want BOTH: (a) a real spotlight cutout over the starter prompts, and
      // (b) the bubble pinned to the TOP of the viewport, wide, never clipped.
      // These are NOT mutually exclusive in this react-joyride fork:
      //
      //  - SPOTLIGHT: the fork renders the spotlight cutout iff
      //    `placement !== "center"` (Overlay: `showSpotlight = ... && placement
      //    !== "center"`). So a REAL side placement produces the cutout, and it
      //    is computed from the TARGET's live DOM rect (`useTargetPosition`),
      //    independent of where the bubble/floater ends up. We use
      //    `placement: "top"` — its natural anchor is ABOVE the target, matching
      //    the direction we then pin the bubble to.
      //  - TOP-PINNED BUBBLE: `data.bubbleAlign: "start"` (honoured by
      //    TourFooter) re-pins the FLOATER element itself to
      //    `position: fixed; top: 16px; left: 50%` via a MutationObserver + rAF.
      //    That re-pin is TARGET-/placement-AGNOSTIC (it keys only off
      //    `bubbleAlign === "start"`), so it moves the bubble to the viewport
      //    top regardless of the "top" placement, and the spotlight cutout stays
      //    on the prompts. `data.wide` widens the bubble (580px vs 360px).
      //
      // No scroll-fight: the Homepage tour sets `skipScroll: true` globally
      // (tourJoyrideStyles), so `needsScrolling` short-circuits — Joyride never
      // scrolls the page for this step, letting the re-pin win cleanly.
      placement: "top",
      title: "Start in plain language",
      content:
        "These starting points place a clear request in the message box. You can edit it, add details, attach a document, and then send it to the assistant.",
      skipBeacon: true,
      data: { bubbleAlign: "start", wide: true },
    });
  }

  // Phone navigation places the Feedback target inside the hamburger menu, so
  // guide the user to open it before targeting that menu item. Desktop and
  // tablet navigation render Feedback directly and deliberately retain the
  // pre-existing step sequence.
  if (mobileNavigation) {
    steps.push(mobileNavigationMenuStep);
  }

  // Feedback-button gate comes BEFORE the chat gate. When the chatbot is
  // DISABLED the chat steps are omitted, so this feedback-button step is the
  // LAST array element BEFORE the user opens the feedback form. Joyride would
  // then label its primary button "Finish" — but the feedback interaction gate
  // (unchanged) greys that button until the form opens, producing a broken-
  // looking greyed "Finish". Instead we push a copy that (a) forces the
  // last-index label to "Next" via `locale.last` (TourFooter reads
  // `step.locale.last` on the last step), and (b) revises the copy to make
  // explicit that opening the Feedback form is what continues the tour. The
  // gate is preserved (`requiresInteraction` + `gateId: "feedback"`), so the
  // button stays greyed until the form opens, at which point `feedbackPanelStep`
  // is appended and becomes the real terminal "Done" step. When the chatbot is
  // ENABLED, the shared `feedbackButtonStep` is used verbatim so the true-path
  // sequence/labels/copy are byte-for-byte identical to before. We never mutate
  // the shared exported const — only push a spread copy in the chat-less branch.
  if (canUseChatbot) {
    steps.push(feedbackButtonStep);
  } else {
    steps.push({
      ...feedbackButtonStep,
      content:
        "Spotted something or have an idea? Click the Feedback button to open the form — you must open it to continue the tour. You can send us a note from any page.",
      locale: { last: "Next" },
    });
  }

  // Feedback-open gate: the feedback button step is ALWAYS present; its in-form
  // follow-up is only appended once the feedback dialog is open. When the chatbot
  // is disabled the chat steps are omitted, so the in-form feedback step becomes
  // the TERMINAL step of the tour — mark its primary button "Done" so the
  // walkthrough finishes cleanly there instead of dead-ending.
  if (feedbackOpen) {
    steps.push(
      canUseChatbot
        ? feedbackPanelStep
        : { ...feedbackPanelStep, locale: { last: "Done" } }
    );
  }

  // When the chatbot is disabled (e.g. RA users) the homepage chat launcher is
  // hidden from the DOM, so we must NOT include the chat-launcher step (it would
  // target a missing element and dead-end on an unclickable "Next") nor the
  // in-panel chat step. The tour ends on the feedback flow instead.
  if (!canUseChatbot) {
    return steps;
  }

  // Chat-launcher gate comes LAST so the chat panel is the terminal step. Next
  // stays greyed out until the chat panel is open.
  steps.push({
    target: '[data-tour-id="home-chat-launcher"]',
    placement: "top",
    title: "Ask the assistant",
    content:
      "Ask a question or describe an acquisition in the message box. To continue the walkthrough, send a message or open Previous chats.",
    skipBeacon: true,
    data: { requiresInteraction: true, gateId: "chat" },
  });

  // Chat-open gate: only reveal the in-panel step once the user has opened the
  // chat (its panel root exists in the DOM). It is the terminal step.
  if (chatOpen) {
    steps.push(homeChatPanelStep);
  }

  return steps;
};
